import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import NotificationApiService, {
  webSocketService,
  UserNotification,
  ApiResponse,
} from "../api/notificationApi";
import { toast } from "sonner";

interface UseUserNotificationsOptions {
  enableWebSocket?: boolean;
  gcTime?: number;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
}

interface UseUserNotificationsReturn {
  notifications: UserNotification[];
  unreadCount: number;
  isLoading: boolean;
  isRefreshing: boolean;
  isCached: boolean;
  lastFetched: Date | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  setUnreadOnly: (unreadOnly: boolean) => void;
  unreadOnly: boolean;
  clearCache: () => void;
  error: Error | null;
}

// Define context types for mutations
interface MarkAsReadContext {
  previousNotifications: unknown;
  previousUnreadCount: unknown;
}

interface MarkAllAsReadContext {
  previousNotifications: unknown;
  previousUnreadCount: unknown;
}

// Query keys
const QUERY_KEYS = {
  NOTIFICATIONS: ["user-notifications"] as const,
  UNREAD_COUNT: ["user-notifications", "unread-count"] as const,
} as const;

export const useUserNotifications = (
  options: UseUserNotificationsOptions = {}
): UseUserNotificationsReturn => {
  const {
    enableWebSocket = true,
    gcTime = 60 * 60 * 1000,
    staleTime = 60 * 60 * 1000,
    refetchOnWindowFocus = false,
    enabled = true,
  } = options;

  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<UserNotification[]>(
    []
  );

  // Query for notifications with proper typing
  const {
    data: notificationsData,
    isLoading: isLoadingNotifications,
    isRefetching: isRefetchingNotifications,
    error: notificationsError,
    refetch: refetchNotifications,
    dataUpdatedAt: notificationsUpdatedAt,
  } = useQuery<ApiResponse<UserNotification[]>, Error>({
    queryKey: [...QUERY_KEYS.NOTIFICATIONS, { unreadOnly, page: currentPage }],
    queryFn: async (): Promise<ApiResponse<UserNotification[]>> => {
      const response = await NotificationApiService.getUserNotifications(
        currentPage,
        20,
        unreadOnly
      );
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch notifications");
      }
      return response;
    },
    enabled,
    gcTime,
    staleTime,
    refetchOnWindowFocus,
  });

  // Query for unread count with proper typing
  const {
    data: unreadCountData,
    isLoading: isLoadingUnreadCount,
    error: unreadCountError,
    refetch: refetchUnreadCount,
  } = useQuery<number, Error>({
    queryKey: QUERY_KEYS.UNREAD_COUNT,
    queryFn: async (): Promise<number> => {
      const response = await NotificationApiService.getUnreadCount();
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch unread count");
      }
      return response.data?.count || 0;
    },
    enabled,
    gcTime,
    staleTime,
    refetchOnWindowFocus,
  });

  // Update notifications state when data changes
  useEffect(() => {
    if (notificationsData?.data) {
      if (currentPage === 1) {
        setAllNotifications(notificationsData.data);
      } else {
        setAllNotifications((prev) => [...prev, ...notificationsData.data]);
      }
    }
  }, [notificationsData, currentPage]);

  // Mutation for marking notification as read
  const markAsReadMutation = useMutation<
    ApiResponse<UserNotification>,
    Error,
    string,
    MarkAsReadContext
  >({
    mutationFn: async (notificationId: string) => {
      const response = await NotificationApiService.markAsRead(notificationId);
      if (!response.success) {
        throw new Error(response.message || "Failed to mark as read");
      }
      return response;
    },
    onMutate: async (notificationId): Promise<MarkAsReadContext> => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });

      const previousNotifications = queryClient.getQueryData([
        ...QUERY_KEYS.NOTIFICATIONS,
        { unreadOnly, page: currentPage },
      ]);
      const previousUnreadCount = queryClient.getQueryData(
        QUERY_KEYS.UNREAD_COUNT
      );

      // Optimistically update notifications
      setAllNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );

      // Optimistically update unread count
      const currentNotification = allNotifications.find(
        (n) => n.id === notificationId
      );
      if (currentNotification && currentNotification.status !== "READ") {
        queryClient.setQueryData(QUERY_KEYS.UNREAD_COUNT, (old: number = 0) =>
          Math.max(0, old - 1)
        );
      }

      return { previousNotifications, previousUnreadCount };
    },
    onError: (error, notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [...QUERY_KEYS.NOTIFICATIONS, { unreadOnly, page: currentPage }],
          context.previousNotifications
        );
      }
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(
          QUERY_KEYS.UNREAD_COUNT,
          context.previousUnreadCount
        );
      }
      toast.error("Failed to mark notification as read");

      setAllNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, isRead: false, readAt: undefined }
            : n
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
    },
  });

  // Mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation<
    ApiResponse<{ updatedCount: number }>,
    Error,
    void,
    MarkAllAsReadContext
  >({
    mutationFn: async () => {
      const response = await NotificationApiService.markAllAsRead();
      if (!response.success) {
        throw new Error(response.message || "Failed to mark all as read");
      }
      return response;
    },
    onMutate: async (): Promise<MarkAllAsReadContext> => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });

      const previousNotifications = queryClient.getQueryData([
        ...QUERY_KEYS.NOTIFICATIONS,
        { unreadOnly, page: currentPage },
      ]);
      const previousUnreadCount = queryClient.getQueryData(
        QUERY_KEYS.UNREAD_COUNT
      );

      setAllNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        }))
      );

      queryClient.setQueryData(QUERY_KEYS.UNREAD_COUNT, 0);

      return { previousNotifications, previousUnreadCount };
    },
    onError: (error, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [...QUERY_KEYS.NOTIFICATIONS, { unreadOnly, page: currentPage }],
          context.previousNotifications
        );
      }
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(
          QUERY_KEYS.UNREAD_COUNT,
          context.previousUnreadCount
        );
      }
      toast.error("Failed to mark all notifications as read");

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
    },
  });

  // WebSocket subscription for real-time updates
  useEffect(() => {
    if (!enableWebSocket) return;

    // Connect to WebSocket first (only if not already connected)
    if (!webSocketService.isConnected()) {
      webSocketService.connect().catch((error) => {
        console.error("Failed to connect to WebSocket:", error);
      });
    }

    const unsubscribe = webSocketService.subscribe("NOTIFICATION", (data) => {
      const notificationData = data.data || data;

      // Create new notification with proper structure
      const newNotification: UserNotification = {
        id: notificationData.id || `ws-${Date.now()}`,
        subject: notificationData.subject || "New Notification",
        body: notificationData.body || notificationData.message || "",
        createdAt: notificationData.createdAt || new Date().toISOString(),
        type: notificationData.type || "SYSTEM",
        status: "DELIVERED",
        sentAt: notificationData.sentAt || new Date().toISOString(),
        deliveredAt: new Date().toISOString(),
      };

      // Update local state immediately with React state updater
      setAllNotifications((prev) => {
        // Avoid duplicates
        const exists = prev.some((n) => n.id === newNotification.id);
        if (exists) {
          return prev;
        }

        // Add to beginning of list
        const updated = [newNotification, ...prev];
        return updated;
      });

      // Update unread count in React Query cache
      queryClient.setQueryData(QUERY_KEYS.UNREAD_COUNT, (old: number = 0) => {
        return old + 1;
      });

      // Update the current query cache if on first page
      if (currentPage === 1) {
        queryClient.setQueryData(
          [...QUERY_KEYS.NOTIFICATIONS, { unreadOnly: unreadOnly, page: 1 }],
          (oldData: ApiResponse<UserNotification[]> | undefined) => {
            if (!oldData) {
              return {
                success: true,
                data: [newNotification],
                pagination: {
                  page: 1,
                  limit: 20,
                  total: 1,
                  totalPages: 1,
                  hasNext: false,
                  hasPrev: false,
                },
              };
            }

            return {
              ...oldData,
              data: [newNotification, ...(oldData.data || [])],
              pagination: oldData.pagination
                ? {
                    ...oldData.pagination,
                    total: oldData.pagination.total + 1,
                  }
                : {
                    page: 1,
                    limit: 20,
                    total: 1,
                    totalPages: 1,
                    hasNext: false,
                    hasPrev: false,
                  },
            };
          }
        );
      }

      // Show toast notification
      // toast.success("New notification received", {
      //   description: newNotification.subject,
      //   duration: 4000,
      // });
    });

    // Subscribe to notification status updates
    const unsubscribeStatus = webSocketService.subscribe(
      "STATUS_UPDATE",
      (data) => {
        if (data.type === "NOTIFICATION_READ" && data.notificationId) {
          setAllNotifications((prev) =>
            prev.map((n) =>
              n.id === data.notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            )
          );

          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
        }
      }
    );

    // Subscribe to connection events
    const unsubscribeConnection = webSocketService.subscribe(
      "CONNECTION",
      (data) => {
        if (data.connected) {
          // ...
        }
      }
    );

    return () => {
      unsubscribe();
      unsubscribeStatus();
      unsubscribeConnection();
    };
  }, [enableWebSocket, queryClient]);

  // Handle filter changes
  useEffect(() => {
    setCurrentPage(1);
    setAllNotifications([]);
  }, [unreadOnly]);

  // Computed values with proper typing
  const notifications = allNotifications;
  const unreadCount = unreadCountData || 0;
  const isLoading = isLoadingNotifications || isLoadingUnreadCount;
  const isRefreshing = isRefetchingNotifications;
  const error = (notificationsError || unreadCountError) as Error | null;
  const isCached =
    !isLoading && notificationsUpdatedAt
      ? Date.now() - notificationsUpdatedAt < staleTime
      : false;
  const lastFetched = notificationsUpdatedAt
    ? new Date(notificationsUpdatedAt)
    : null;

  // Safe pagination access with fallbacks
  const pagination = {
    page: notificationsData?.pagination?.page || 1,
    limit: notificationsData?.pagination?.limit || 20,
    total: notificationsData?.pagination?.total || 0,
    totalPages: notificationsData?.pagination?.totalPages || 0,
    hasNext: notificationsData?.pagination?.hasNext || false,
    hasPrev: notificationsData?.pagination?.hasPrev || false,
  };

  // Action handlers
  const markAsRead = useCallback(
    async (id: string) => {
      try {
        await markAsReadMutation.mutateAsync(id);
      } catch (error) {
        console.error("Error marking notification as read:", error);
        throw error;
      }
    },
    [markAsReadMutation]
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      throw error;
    }
  }, [markAllAsReadMutation]);

  const refreshNotifications = useCallback(async () => {
    try {
      await Promise.all([refetchNotifications(), refetchUnreadCount()]);
    } catch (error) {
      console.error("Error refreshing notifications:", error);
      toast.error("Failed to refresh notifications");
    }
  }, [refetchNotifications, refetchUnreadCount]);

  const forceRefresh = useCallback(async () => {
    try {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
      await refreshNotifications();
    } catch (error) {
      console.error("Error force refreshing notifications:", error);
      toast.error("Failed to refresh notifications");
    }
  }, [queryClient, refreshNotifications]);

  const loadMore = useCallback(async () => {
    if (pagination.hasNext) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [pagination.hasNext]);

  const clearCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
    queryClient.removeQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
    queryClient.removeQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
    setAllNotifications([]);
    setCurrentPage(1);
  }, [queryClient]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isRefreshing,
    isCached,
    lastFetched,
    pagination,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    forceRefresh,
    loadMore,
    setUnreadOnly,
    unreadOnly,
    clearCache,
    error,
  };
};
